/* eslint no-console: "off" */
import { EventEmitter } from "events";
import type { LogEntry } from "@/framework/logger/local/LogEntry";
import type { LogOptions } from "@/framework/logger/local/LogOptions";
import { formatJSON } from "@/framework/utilities/formatter";

/**
 * Handles the logging
 */
export class Logger {
  private logManager: EventEmitter;
  private readonly minLevel: number;
  private readonly module: string;
  private readonly levels: { [key: string]: number } = {
    trace: 1,
    debug: 2,
    info: 3,
    warn: 4,
    error: 5,
  };

  constructor(logManager: EventEmitter, module: string, minLevel: string) {
    this.logManager = logManager;
    this.module = module;
    this.minLevel = this.levelToInt(minLevel);
  }

  /**
   * Central logging method.
   * @param logLevel
   * @param message
   */
  public log(logLevel: string, message: any): void {
    const level = this.levelToInt(logLevel);
    if (level < this.minLevel) return;

    let messageString = message;

    if (typeof message === "object") {
      messageString = formatJSON(JSON.stringify(message));
    }

    const logEntry: LogEntry = {
      level: logLevel,
      module: this.module,
      message: messageString,
    };

    // Obtain the line/file through a thoroughly hacky method
    // This creates a new stack trace and pulls the caller from it.  If the caller
    // is .trace()
    const error = new Error("");
    if (error.stack) {
      const cla = error.stack.split("\n");
      let idx = 3;
      while (idx < cla.length && cla[idx].includes("at Logger.Object.")) idx++;
      if (idx < cla.length) {
        logEntry.location = cla[idx].slice(cla[idx].indexOf("at ") + 3, cla[idx].length).replace("setup", "log");
      }
    }
    this.logManager.emit("log", logEntry);
  }

  public trace(message: any): void {
    this.log("trace", message);
  }

  public debug(message: any): void {
    this.log("debug", message);
  }

  public info(message: any): void {
    this.log("info", message);
  }

  public warn(message: any): void {
    this.log("warn", message);
  }

  public error(message: any): void {
    this.log("error", message);
  }

  /**
   * Converts a string level (trace/debug/info/warn/error) into a number
   *
   * @param minLevel
   */
  private levelToInt(minLevel: string): number {
    if (minLevel.toLowerCase() in this.levels) return this.levels[minLevel.toLowerCase()];
    else return 99;
  }
}

/**
 * Handles configuration and management of the logging system
 *
 * ### How to use the logger
 * 1. Import logging in the file you want to log

 *  `import { logging } from "@/framework/logger/LogManager";`
 *
 * 2. Instantiate the logger with the module or file you are trying to log
 *
 * `const logger = logging.getLogger("Views.AboutView");`
 *
 * 3. Use the logger
 * ```logger.info(`This is my log message`);```
 *
 *
 * Source: https://adrianhall.github.io/cloud/2019/06/30/building-an-efficient-logger-in-typescript/
 */
export class LogManager extends EventEmitter {
  private options: LogOptions = {
    minLevels: {
      "": "info",
    },
  };

  // Prevent the console logger from being added twice
  private consoleLoggerRegistered: boolean = false;

  public configure(options: LogOptions): LogManager {
    this.options = Object.assign({}, this.options, options);
    return this;
  }

  /**
   *
   * @param module Module, file or component that helps to indentify area where the entry is logged (e.g. 'core', 'auth.services.signup' or 'aview.login')
   */
  public getLogger(module: string = ""): Logger {
    let minLevel = "none";
    let match = "";

    for (const key in this.options.minLevels) {
      if (module.startsWith(key) && key.length >= match.length) {
        minLevel = this.options.minLevels[key];
        match = key;
      }
    }

    return new Logger(this, module, minLevel);
  }

  public onLogEntry(listener: (logEntry: LogEntry) => void): LogManager {
    this.on("log", listener);
    return this;
  }

  public registerConsoleLogger(): LogManager {
    if (this.consoleLoggerRegistered) return this;

    this.onLogEntry((logEntry) => {
      const msg = logEntry.module
        ? `[${logEntry.module}] ${logEntry.message} \n ${logEntry.location} `
        : `${logEntry.message} \n ${logEntry.location} `;

      switch (logEntry.level) {
        case "trace":
          console.trace(msg);
          break;
        case "debug":
          console.debug(msg);
          break;
        case "info":
          console.info(msg);
          break;
        case "warn":
          console.warn(msg);
          break;
        case "error":
          console.error(msg);
          break;
        default:
          console.log(`{${logEntry.level}} ${msg}`);
      }
    });

    this.consoleLoggerRegistered = true;
    return this;
  }
}

export const logging = new LogManager();
